import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import bookingApi from "../api/booking";
import AlertMessage from "../components/AlertMessage";
import Loader from "../components/Loader";
import Modal from "../components/Modal";
import { Calendar, Users, DollarSign, MapPin, AlertTriangle } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

export default function UserBookingsPage() {
  const { settings } = useSettings();
  const cs = settings.currency_symbol || '$';
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  useEffect(() => {
    loadBookings();

    // ✅ Afficher le message de succès si passé depuis BookingPage
    if (location.state?.successMessage) {
      setSuccess(location.state.successMessage);
      // Nettoyer le state pour éviter que le message persiste
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const loadBookings = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await bookingApi.getUserBookings();
      setBookings(data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load your bookings."
      );
    } finally {
      setLoading(false);
    }
  };

  const openCancelModal = (booking) => {
    setBookingToCancel(booking);
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setBookingToCancel(null);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;

    setCancellingId(bookingToCancel.id);
    setError("");
    setSuccess("");
    setShowCancelModal(false);

    try {
      const { data } = await bookingApi.cancelBooking(bookingToCancel.id);
      // Backend returns { message, booking }
      const updatedBooking = data.booking || data;

      setBookings((prev) =>
        prev.map((b) => (b.id === bookingToCancel.id ? updatedBooking : b))
      );
      setSuccess("Booking cancelled successfully!");
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to cancel booking."
      );
    } finally {
      setCancellingId(null);
      setBookingToCancel(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-800 dark:text-yellow-300",
        label: "Pending",
      },
      confirmed: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-800 dark:text-green-300",
        label: "Confirmed",
      },
      cancelled: {
        bg: "bg-red-100 dark:bg-red-900/30",
        text: "text-red-800 dark:text-red-300",
        label: "Cancelled",
      },
      completed: {
        bg: "bg-blue-100 dark:bg-blue-900/30",
        text: "text-blue-800 dark:text-blue-300",
        label: "Completed",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
          My Bookings
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-300">
          View and manage your tour bookings
        </p>
      </div>

      {error && (
        <AlertMessage type="error" onClose={() => setError("")}>
          {error}
        </AlertMessage>
      )}

      {success && (
        <AlertMessage type="success" onClose={() => setSuccess("")}>
          {success}
        </AlertMessage>
      )}

      {bookings.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
            <Calendar className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            No bookings yet
          </h3>
          <p className="text-slate-600 dark:text-slate-300">
            You haven't made any tour bookings. Explore our tours and book your
            next adventure!
          </p>
          <a
            href="/tours"
            className="mt-4 inline-block rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            Browse Tours
          </a>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
            >
              {/* Booking Details */}
              <div className="p-5 space-y-4">
                {/* Status Badge at Top */}
                <div className="flex justify-end">
                  {getStatusBadge(booking.status)}
                </div>
                {/* Tour Title */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {booking.tour?.title || "Tour"}
                  </h3>
                  {booking.tour?.city && (
                    <p className="mt-1 flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300">
                      <MapPin size={14} />
                      {booking.tour.city}
                    </p>
                  )}
                </div>

                {/* Booking Info */}
                <div className="space-y-2 border-t border-slate-200 pt-4 dark:border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <Calendar size={16} />
                      Date
                    </span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {new Date(booking.booking_date).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <Users size={16} />
                      Travelers
                    </span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {booking.number_of_people}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <DollarSign size={16} />
                      Total
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      {cs}{booking.total_price}
                    </span>
                  </div>
                </div>

                {/* Payment Info */}
                {(booking.amount_paid > 0 || booking.deposit_amount > 0) && (
                  <div className="space-y-2 border-t border-slate-200 pt-4 dark:border-slate-700">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-300">Deposit Paid</span>
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {cs}{booking.amount_paid || 0}
                      </span>
                    </div>
                    {booking.remaining_amount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-300">Remaining</span>
                        <span className="font-medium text-amber-600 dark:text-amber-400">
                          {cs}{booking.remaining_amount}
                        </span>
                      </div>
                    )}
                    {booking.payment_method && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-300">Paid via</span>
                        <span className="capitalize font-medium text-slate-900 dark:text-slate-100">
                          {booking.payment_method}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Info */}
                <div className="space-y-1 border-t border-slate-200 pt-4 dark:border-slate-700">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Contact: {booking.email}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Phone: {booking.phone}
                  </p>
                </div>

                {/* Actions */}
                {booking.status === "pending" ||
                booking.status === "confirmed" ? (
                  <button
                    onClick={() => openCancelModal(booking)}
                    disabled={cancellingId === booking.id}
                    className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                  >
                    {cancellingId === booking.id
                      ? "Cancelling..."
                      : "Cancel Booking"}
                  </button>
                ) : booking.status === "cancelled" ? (
                  <div className="rounded-lg bg-slate-100 p-3 text-center text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    This booking has been cancelled
                  </div>
                ) : (
                  <div className="rounded-lg bg-blue-100 p-3 text-center text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    This booking is completed
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={closeCancelModal}
        title="Cancel Booking"
        variant="danger"
      >
        <div className="space-y-4">
          {/* Warning Icon */}
          <div className="flex justify-center">
            <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Warning Message */}
          <div className="text-center">
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Are you sure you want to cancel this booking?
            </p>
            {bookingToCancel && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {bookingToCancel.tour?.title || "Tour"}
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  {new Date(bookingToCancel.booking_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Warning Text */}
          <p className="text-center text-sm text-slate-600 dark:text-slate-300">
            This action cannot be undone. Your booking will be marked as cancelled.
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={closeCancelModal}
              className="flex-1 rounded-lg border-2 border-slate-300 px-4 py-2 font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Keep Booking
            </button>
            <button
              onClick={confirmCancelBooking}
              className="flex-1 rounded-lg bg-red-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-red-700"
            >
              Yes, Cancel It
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
